import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import ISelectOption from "../../models/view/common/ISelectOption";
import {getAudioContext} from "../../services/audioContext";
import {defaultVolumes} from "./settingsDefaults";
import {IVolumes} from "../types/IVolumes";
import {NormalizedMidiInputArray} from "../../models/NormalizedMidiInputArray";
import {PDCStatus} from "../types/pdcStatus";

export interface ISettingsStoreState {
  isMidiDeviceConnected: boolean;
  isPitchDetectionDebuggingEnabled: boolean;
  isUMPConsentRequired: boolean;
  isPdcConsentApplicable: boolean;
  lessonPlayer: {
    volumes: IVolumes;
  }
  midiSettings:{
    midiDeviceOptions: NormalizedMidiInputArray;
    currentlySelectedMidiDeviceId: string;
    currentlySelectedMidiDeviceName: string;
  },
  pdcStatus: PDCStatus;
  playbackSettings: {
    webAudioSampleRate: number;
    sampleRateOptions: ISelectOption[];
    currentlySelectedSampleRate: string;
    soundLibraryOptions: ISelectOption[];
    currentlySelectedSoundLibrary: string;
  },
  virtualPianoNumberOfKeys: number | string;
}

const settingsStoreInitialState: ISettingsStoreState = {
  isMidiDeviceConnected: false,
  isPitchDetectionDebuggingEnabled: false,
  isUMPConsentRequired: false,
  isPdcConsentApplicable: true, // The Disclosure and Consent page will not show up if this defaults to false.
  lessonPlayer: {
    volumes: { ...defaultVolumes }
  },
  midiSettings: {
    currentlySelectedMidiDeviceId: "",
    currentlySelectedMidiDeviceName: "",
    midiDeviceOptions: new NormalizedMidiInputArray(),
  },
  pdcStatus: "unknown",
  playbackSettings: {
    currentlySelectedSampleRate: `${getAudioContext().sampleRate}`,
    currentlySelectedSoundLibrary: "Alicia 44k Low Quality",
    sampleRateOptions: [
      {label: "44100", value: "44100"},
      {label: "48000", value: "48000"},
      {label: "88200", value: "88200"},
      {label: "96000", value: "96000"},
      {label: "176400", value: "176400"},
    ],
    soundLibraryOptions: [
      {label: "Guitar 44k Low Quality", value: "Guitar 44k Low Quality"},
      {label: "Alicia 48k Medium Quality", value: "Alicia 48k Medium Quality"},
      {label: "Alicia 48k Low Quality", value: "Alicia 48k Low Quality"},
      {label: "Legacy 44.1k", value: "Legacy 44.1k"},
    ],
    webAudioSampleRate: getAudioContext().sampleRate,
  },
  virtualPianoNumberOfKeys: 88,
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: settingsStoreInitialState,
  reducers: (create) => ({
    backingTrackVolumeReducer: create.reducer((state, action: PayloadAction<{volume: number}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, backingTrack: action.payload.volume} };
    }),
    isBackingTrackMutedReducer: create.reducer((state, action: PayloadAction<{isMuted: boolean}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, isBackingTrackMuted: action.payload.isMuted} };
    }),
    countInVolumeReducer: create.reducer((state, action: PayloadAction<{volume: number}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, countIn: action.payload.volume} };
    }),
    isCountInMutedReducer: create.reducer((state, action: PayloadAction<{isMuted: boolean}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, isCountInMuted: action.payload.isMuted} };
    }),
    isUMPConsentRequiredReducer: create.reducer((state, action: PayloadAction<{isRequired: boolean}>) => {
      state.isUMPConsentRequired = action.payload.isRequired;
    }),
    isPdcConsentApplicableReducer: create.reducer((state, action: PayloadAction<{isApplicable: boolean}>) => {
      state.isPdcConsentApplicable = action.payload.isApplicable;
    }),
    metronomeVolumeReducer: create.reducer((state, action: PayloadAction<{volume: number}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, metronome: action.payload.volume} };
    }),
    isMetronomeMutedReducer: create.reducer((state, action: PayloadAction<{isMuted: boolean}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, isMetronomeMuted: action.payload.isMuted} };
    }),
    videoVolumeReducer: create.reducer((state, action: PayloadAction<{volume: number}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, video: action.payload.volume} };
    }),
    pianoVolumeReducer: create.reducer((state, action: PayloadAction<{volume: number}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, piano: action.payload.volume} };
    }),
    volumesReducer: create.reducer((state, action: PayloadAction<{volumes: IVolumes}>) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...state.lessonPlayer.volumes, ...action.payload.volumes} };
    }),
    resetVolumesReducer: create.reducer((state) => {
      state.lessonPlayer = { ...state.lessonPlayer, volumes: {...defaultVolumes} };
    }),
    selectedMidiDeviceIdReducer: create.reducer((state, action: PayloadAction<{deviceId: string}>) => {
      state.midiSettings = {...state.midiSettings, currentlySelectedMidiDeviceId: action.payload.deviceId};
    }),
    selectedMidiDeviceNameReducer: create.reducer((state, action: PayloadAction<{deviceName: string}>) => {
      state.midiSettings = {...state.midiSettings, currentlySelectedMidiDeviceName: action.payload.deviceName};
    }),
    currentlySelectedSoundLibraryReducer: create.reducer((state, action: PayloadAction<{library: string}>) => {
      state.playbackSettings = {...state.playbackSettings, currentlySelectedSoundLibrary: action.payload.library};
    }),
    isMidiDeviceConnectedReducer: create.reducer((state, action: PayloadAction<{isConnected: boolean}>) => {
      state.isMidiDeviceConnected = action.payload.isConnected;
    }),
    isPitchDetectionDebuggingEnabledReducer: create.reducer((state, action: PayloadAction<{isEnabled: boolean}>) => {
      state.isPitchDetectionDebuggingEnabled = action.payload.isEnabled;
    }),
    midiDeviceOptionsReducer: create.reducer((state, action: PayloadAction<{options: NormalizedMidiInputArray}>) => {
      state.midiSettings = {...state.midiSettings, midiDeviceOptions: action.payload.options};
    }),
    selectedNumberOfKeysReducer: create.reducer((state, action: PayloadAction<{numberOfKeys: number | string}>) => {
      state.virtualPianoNumberOfKeys = action.payload.numberOfKeys;
    }),
    pdcStatusReducer: create.reducer((state, action: PayloadAction<{status: PDCStatus}>) => {
      state.pdcStatus = action.payload.status;
    }),
  }),

  selectors: {
    isBackingTrackMutedSelector: (state: ISettingsStoreState) => state.lessonPlayer.volumes.isBackingTrackMuted,
    isCountInMutedSelector: (state: ISettingsStoreState) => state.lessonPlayer.volumes.isCountInMuted,
    isMetronomeMutedSelector: (state: ISettingsStoreState) => state.lessonPlayer.volumes.isMetronomeMuted,
    isMidiDeviceConnectedSelector: (state: ISettingsStoreState) => state.isMidiDeviceConnected,
    isPitchDetectionDebuggingEnabledSelector: (state: ISettingsStoreState) => state.isPitchDetectionDebuggingEnabled,
    isUMPConsentRequiredSelector: (state: ISettingsStoreState) => state.isUMPConsentRequired,
    isPdcConsentApplicableSelector: (state: ISettingsStoreState) => state.isPdcConsentApplicable,
    midiDeviceOptionsSelector: (state: ISettingsStoreState) => state.midiSettings.midiDeviceOptions,
    selectedMidiDeviceIdSelector: (state: ISettingsStoreState) => state.midiSettings.currentlySelectedMidiDeviceId,
    selectedMidiDeviceNameSelector: (state: ISettingsStoreState) => state.midiSettings.currentlySelectedMidiDeviceName,
    pdcStatusSelector: (state: ISettingsStoreState) => state.pdcStatus,
    virtualPianoNumberOfKeysSelector: (state: ISettingsStoreState) => state.virtualPianoNumberOfKeys,
    volumesSelector: (state: ISettingsStoreState) => state.lessonPlayer.volumes,
  }
});

export const settingsReducer = settingsSlice.reducer;
